import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  Button,
  IconButton,
  Avatar,
  Breadcrumbs,
  Link,
  Paper,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  QrCode as QrCodeIcon,
  Home as HomeIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchMachine, clearCurrentMachine, deleteMachine } from '../../store/slices/machinesSlice';
import { MachineOverviewTab } from '../../components/machines/MachineOverviewTab';
import { MachineManualsTab } from '../../components/machines/MachineManualsTab';
import { MachineSparePartsTab } from '../../components/machines/MachineSparePartsTab';
import { MachineOperatingSuppliesTab } from '../../components/machines/MachineOperatingSuppliesTab';
import { MachineToolsTab } from '../../components/machines/MachineToolsTab';
import { format, isValid, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`machine-tabpanel-${index}`}
      aria-labelledby={`machine-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const statusColors = {
  active: 'success',
  maintenance: 'warning',
  inactive: 'error',
} as const;

const statusLabels = {
  active: 'Aktiv',
  maintenance: 'Wartung',
  inactive: 'Inaktiv',
};

// Helper function to safely format dates
const formatDate = (dateString: string | undefined | null): string => {
  // Handle all falsy values FIRST
  if (!dateString || dateString === 'null' || dateString === 'undefined' || dateString === '') {
    return 'Nicht verfügbar';
  }
  
  // NEVER call new Date() or parseISO() without extensive validation
  try {
    // Convert to string and validate format
    const strDate = String(dateString).trim();
    
    // Check for common invalid patterns
    if (strDate === '0001-01-01T00:00:00Z' || 
        strDate === '0000-00-00T00:00:00Z' ||
        strDate.length < 10 ||
        !strDate.includes('T') && !strDate.includes(' ')) {
      return 'Nicht verfügbar';
    }
    
    // Use parseISO which is safer than new Date()
    const date = parseISO(strDate);
    
    // Multiple validation checks
    if (!date || !isValid(date) || isNaN(date.getTime()) || date.getFullYear() < 1900 || date.getFullYear() > 2100) {
      return 'Ungültiges Datum';
    }
    
    return format(date, 'dd.MM.yyyy', { locale: de });
  } catch (error) {
    // Log error but don't throw
    console.warn('Date formatting error for value:', dateString, error);
    return 'Datum Fehler';
  }
};

export const MachineDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { currentMachine, isLoading, error } = useAppSelector((state) => state.machines);
  const { user } = useAppSelector((state) => state.auth);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Debug: Log machine data whenever it changes
  React.useEffect(() => {
    if (currentMachine) {
      console.log('=== MACHINE DATA DEBUG ===');
      console.log('Full machine object:', currentMachine);
      console.log('Created at raw:', currentMachine.createdAt, typeof currentMachine.createdAt);
      console.log('Updated at raw:', currentMachine.updatedAt, typeof currentMachine.updatedAt);
      console.log('========================');
    }
  }, [currentMachine]);
  
  // Determine initial tab based on query parameter
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    switch (tabParam) {
      case 'manuals': return 1;
      case 'spare-parts': return 2;
      case 'supplies': return 3;
      case 'tools': return 4;
      default: return 0;
    }
  };
  
  const [tabValue, setTabValue] = useState(getInitialTab());

  // Update tab when search params change
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    let newTabValue = 0;
    switch (tabParam) {
      case 'manuals': newTabValue = 1; break;
      case 'spare-parts': newTabValue = 2; break;
      case 'supplies': newTabValue = 3; break;
      case 'tools': newTabValue = 4; break;
      default: newTabValue = 0; break;
    }
    setTabValue(newTabValue);
  }, [searchParams]);

  useEffect(() => {
    if (id) {
      dispatch(fetchMachine(id));
    }
    
    return () => {
      dispatch(clearCurrentMachine());
    };
  }, [dispatch, id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Update URL parameters to match the selected tab
    const tabMapping = ['', 'manuals', 'spare-parts', 'supplies', 'tools'];
    const tabParam = tabMapping[newValue];
    
    if (tabParam) {
      // Navigate to the specific tab
      navigate(`${window.location.pathname}?tab=${tabParam}`, { replace: true });
    } else {
      // Navigate back to overview (clear tab parameter)
      navigate(window.location.pathname, { replace: true });
    }
  };

  const handleBack = () => {
    navigate('/machines');
  };

  const handleEdit = () => {
    if (currentMachine) {
      navigate(`/machines/${currentMachine.id}/edit`);
    }
  };

  const handleShowQR = () => {
    if (currentMachine) {
      navigate(`/machines/${currentMachine.id}/qr`);
    }
  };

  const handleDeleteRequest = () => {
    setDeleteDialogOpen(true);
    setDeleteError(null);
  };

  const handleDeleteCancel = () => {
    if (isDeleting) {
      return;
    }
    setDeleteDialogOpen(false);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!currentMachine) {
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);
    try {
      await dispatch(deleteMachine(currentMachine.id)).unwrap();
      setDeleteDialogOpen(false);
      navigate('/machines');
    } catch (deleteErr: any) {
      setDeleteError(typeof deleteErr === 'string' ? deleteErr : 'Löschen der Maschine fehlgeschlagen');
    } finally {
      setIsDeleting(false);
    }
  };

  const canEdit = user?.role === 'admin';

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBack}>
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  if (isLoading || !currentMachine) {
    return (
      <Box>
        {/* Header Skeleton */}
        <Box display="flex" alignItems="center" mb={2}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={200} height={24} />
        </Box>
        
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Skeleton variant="rectangular" height={300} />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="text" width="40%" height={24} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={200} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  const tabs = [
    { label: 'Übersicht', component: MachineOverviewTab },
    { label: 'Anleitungen', component: MachineManualsTab },
    { label: 'Ersatzteile', component: MachineSparePartsTab },
    { label: 'Betriebsmittel', component: MachineOperatingSuppliesTab },
    { label: 'Werkzeuge', component: MachineToolsTab },
  ];

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={handleBack}
        >
          Maschinen
        </Link>
        <Typography color="text.primary">{currentMachine.name}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {currentMachine.name}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label={statusLabels[currentMachine.status]}
                color={statusColors[currentMachine.status]}
                size="small"
              />
              <Typography variant="body2" color="text.secondary">
                {currentMachine.location || 'Kein Standort angegeben'}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Box display="flex" gap={1}>
          <IconButton onClick={handleShowQR} title="QR-Code anzeigen">
            <QrCodeIcon />
          </IconButton>
          {canEdit && (
            <>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteRequest}
              >
                Löschen
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Bearbeiten
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Machine Header Card */}
      <Paper sx={{ mb: 3 }}>
        <Grid container>
          <Grid size={{ xs: 12, md: 4 }}>
            <CardMedia
              sx={{
                height: 300,
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {currentMachine.imageUrl ? (
                <img
                  src={currentMachine.imageUrl}
                  alt={currentMachine.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    bgcolor: 'primary.main',
                    fontSize: '3rem',
                  }}
                >
                  {currentMachine.name.charAt(0).toUpperCase()}
                </Avatar>
              )}
            </CardMedia>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <CardContent sx={{ height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Beschreibung
              </Typography>
              <Typography variant="body1" paragraph>
                {currentMachine.description || 'Keine Beschreibung verfügbar.'}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body1">
                    {statusLabels[currentMachine.status]}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Standort
                  </Typography>
                  <Typography variant="body1">
                    {currentMachine.location || 'Nicht angegeben'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Erstellt
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(currentMachine?.createdAt)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Aktualisiert
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(currentMachine?.updatedAt)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="machine detail tabs"
          >
            {tabs.map((tab, index) => (
              <Tab
                key={tab.label}
                label={tab.label}
                id={`machine-tab-${index}`}
                aria-controls={`machine-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        {tabs.map((tab, index) => (
          <TabPanel key={tab.label} value={tabValue} index={index}>
            <tab.component machine={currentMachine} />
          </TabPanel>
        ))}
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Maschine löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie diese Maschine dauerhaft löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.
          </DialogContentText>
          {deleteError && (
            <DialogContentText color="error" sx={{ mt: 2 }}>
              {deleteError}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Löschen…' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
